import type {ModelPatchOp} from '@/types/api';

/** Нормализует объект/массив для стабильного JSON (сортировка ключей). Нужно для сравнения "наш патч" при эхо с сервера. */
function normalizeForJson(value: unknown): unknown {
	if (value === null || typeof value !== 'object') return value;
	if (Array.isArray(value)) return value.map(normalizeForJson);
	const sorted: Record<string, unknown> = {};
	for (const k of Object.keys(value as Record<string, unknown>).sort()) {
		sorted[k] = normalizeForJson((value as Record<string, unknown>)[k]);
	}
	return sorted;
}

/** Стабильная строка для массива патчей (одинаковый порядок ключей при любом сериализаторе). */
export function patchFingerprint(patches: ModelPatchOp[]): string {
	return JSON.stringify(normalizeForJson(patches));
}

export function getPatchDedupKey(op: ModelPatchOp): string {
	const v = op?.viewIndex ?? 0;
	switch (op?.op) {
		case 'addNode':
			return `addNode:${v}:${op.node?.identifier ?? ''}`;
		case 'addConnection':
			return `addConnection:${v}:${op.connection?.identifier ?? ''}`;
		case 'removeNode':
		case 'updateNode':
		case 'updateConnection':
		case 'updateElementLabel':
		case 'updateElementProperties':
		case 'updateRelationship':
		case 'updateNodeStyles':
			return `${op.op}:${v}:${op.identifier ?? ''}`;
		case 'removeConnection':
			return `removeConnection:${v}:${op.identifier ?? ''}`;
		case 'removeRelationshipFromModel':
		case 'removeTemporaryRelationshipFromModel':
			return `${op.op}:${op.identifier ?? ''}`;
		case 'addView':
			return `addView:${op.viewIdentifier ?? ''}`;
		case 'updateViewName':
			return `updateViewName:${op.viewIdentifier ?? ''}`;
		case 'removeView':
			return `removeView:${op.viewIdentifier ?? ''}`;
		default:
			return `misc:${JSON.stringify(op)}`;
	}
}

/** Слияние патчей с одним dedup-ключом (например embedded + move не теряют parent). */
export function mergePatchForDedupKey(prev: ModelPatchOp, next: ModelPatchOp): ModelPatchOp {
	if (prev.op !== next.op) {
		return next;
	}
	if (next.op === 'updateNode') {
		return {
			...prev,
			...next,
			viewIndex: next.viewIndex !== undefined ? next.viewIndex : prev.viewIndex,
			identifier: next.identifier !== undefined ? next.identifier : prev.identifier,
			x: next.x !== undefined ? next.x : prev.x,
			y: next.y !== undefined ? next.y : prev.y,
			w: next.w !== undefined ? next.w : prev.w,
			h: next.h !== undefined ? next.h : prev.h,
			parentNodeId:
				next.parentNodeId !== undefined ? next.parentNodeId : prev.parentNodeId,
			includeParent:
				next.includeParent === true || prev.includeParent === true ? true : undefined,
			fillColor: next.fillColor !== undefined ? next.fillColor : prev.fillColor,
			lineColor: next.lineColor !== undefined ? next.lineColor : prev.lineColor,
			lineWidth: next.lineWidth !== undefined ? next.lineWidth : prev.lineWidth,
			styles: next.styles !== undefined ? next.styles : prev.styles,
		};
	}
	return next;
}

export function deduplicatePatches(patches: ModelPatchOp[]): ModelPatchOp[] {
	const lastByKey = new Map<string, {patch: ModelPatchOp; index: number}>();
	patches.forEach((p, i) => {
		const key = getPatchDedupKey(p);
		lastByKey.set(key, {patch: p, index: i});
	});
	return Array.from(lastByKey.entries())
		.sort((a, b) => a[1].index - b[1].index)
		.map(([, {patch}]) => patch);
}
